import React from "react";
import Modal from "../common/Modal";
import refundImage from "../../images/Refund & Cancellation.webp";
import styles from "./RefundPolicyModal.module.scss";

const refundCards = [
  { number: "1", icon: "📄", title: "Nature of Our Services", content: "We provide business reporting, analytics, dashboards, advisory support, and clarity-driven operational services." },
  { number: "2", icon: "🛡️", title: "General Refund Principle", content: "Refunds are generally not applicable once onboarding, setup, reporting, or business review work has started." },
  { number: "3", icon: "📅", title: "What “Service Commencement” Means", content: "Service commencement means onboarding, setup, access provisioning, review activity, or reporting work has started." },
  { number: "4", icon: "⏳", title: "Cancellation Before Service Commencement", content: "Refunds before commencement may be considered subject to deductions for payment gateway and admin charges." },
  { number: "5", icon: "ℹ️", title: "Cancellation After Service Commencement", content: "Once service effort has started, refund eligibility becomes highly restricted." },
  { number: "6", icon: "📁", title: "Customised and Prepared Work Is Generally Non-Refundable", content: "Custom reports, prepared dashboards, configured access, and structured business material are non-refundable." },
  { number: "7", icon: "📅", title: "Recurring Plans, Weekly Plans, Monthly Plans, and Auto-Debit", content: "Cancellations apply to future cycles only; currently active cycles and automated renewals are non-refundable." },
  { number: "8", icon: "ℹ️", title: "Non-Usage Does Not Automatically Create Refund Eligibility", content: "Refunds are not granted solely based on non-usage, failure to log in, or delayed cooperation from your side." },
  { number: "9", icon: "⏳", title: "Delay or Non-Cooperation from Client Side", content: "Delays or interruptions caused by missing data or non-responsiveness from your team do not create refund eligibility." },
  { number: "10", icon: "📄", title: "No Refund Based on Business Results or Expectations", content: "Services are provided for business clarity and support, not for guaranteed financial outcomes or profit increases." },
  { number: "11", icon: "📁", title: "Duplicate Payments and Excess Payments", content: "Excess or duplicate payments will be refunded after verification, subject to applicable deductions." },
  { number: "12", icon: "🛡️", title: "Failed Transactions and Payment Errors", content: "Munafa Engine is not responsible for third-party delays in resolving failed transactions or payment errors." },
  { number: "13", icon: "⏳", title: "Refund Mode and Processing Time", content: "Approved refunds are processed through the original payment mode, with timelines depending on banking cycles." },
  { number: "14", icon: "📄", title: "Payment Gateway Charges, Taxes, and Non-Recoverable Deductions", content: "Approved refunds may be subject to deductions for non-recoverable charges, taxes, and incurred costs." },
  { number: "15", icon: "🛡️", title: "Chargebacks and Payment Disputes", content: "Initiating a chargeback while service has commenced does not entitle you to an automatic refund." },
  { number: "16", icon: "ℹ️", title: "Cancellation, Suspension, or Termination by Munafa Engine", content: "We may suspend or terminate service for misuse or compliance risks, with refunds determined by the completion stage." },
  { number: "17", icon: "🛡️", title: "Service Access Suspension and Its Refund Effect", content: "Suspensions due to terms violations or unauthorized access do not automatically make you eligible for a refund." },
  { number: "18", icon: "📁", title: "Goodwill, Exception-Based, or Discretionary Refunds", content: "Exception-based refunds may be offered entirely at our discretion and do not create a precedent." },
  { number: "19", icon: "⏳", title: "No Waiver by Delayed Action", content: "Delayed enforcement of this policy does not constitute a waiver of our rights in future situations." },
  { number: "20", icon: "📄", title: "Changes to This Policy", content: "This policy may be updated periodically to reflect changes in our services, compliance, or billing structures." },
  { number: "21", icon: "ℹ️", title: "Contact for Cancellation or Refund Requests", content: "Please contact us at connect@munafaengine.com with relevant details for any cancellation or refund requests." },
  { number: "22", icon: "📄", title: "Final Note", content: "By making a payment or starting an engagement, you fully acknowledge and agree to this policy." }
];

export default function RefundPolicyModal({
  isOpen,
  onClose,
  onOpenTerms,
  onOpenPrivacy
}) {
  const scrollToClause = (id) => {
    const element = document.getElementById(id);

    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className={styles.refundModal}>
        <div className={styles.mainContent}>

          {/* LEFT */}
          <div className={styles.leftSection}>

            <div className={styles.badge}>
              LEGAL PAGE
            </div>

            <h1 className={styles.heroTitle}>
              Refund & Cancellation Policy
            </h1>

            <p className={styles.description}>
              This Refund & Cancellation Policy explains
              how cancellations, rescheduling,
              recurring billing, auto-debit,
              and refund requests are handled
              for services offered under Munafa Engine.
            </p>

            <p className={styles.description}>
              By making a payment, onboarding,
              or continuing with a paid engagement,
              you agree to this Refund & Cancellation Policy
              along with our Terms & Conditions
              and Privacy Policy.
            </p>

            <div className={styles.quickCard}>

              <div className={styles.quickHeader}>
                <h3>Quick Navigation</h3>

                <span>22 Clauses</span>
              </div>

              <div className={styles.quickGrid}>
                {refundCards.map((item, index) => (
                  <button
                    key={index}
                    className={styles.quickItem}
                    onClick={() =>
                      scrollToClause(
                        `refund-${item.number}`
                      )
                    }
                  >
                    <span>
                      {item.number.padStart(
                        2,
                        "0"
                      )}
                    </span>

                    <p>{item.title}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div className={styles.rightSection}>

            <div className={styles.imageWrapper}>
              <img
                src={refundImage}
                alt="Refund & Cancellation Policy"
                className={styles.heroImage}
               loading="lazy" decoding="async"/>
            </div>

            <div className={styles.cards}>
              {refundCards.map((item, index) => (
                <div
                  key={index}
                  id={`refund-${item.number}`}
                  className={styles.termCard}
                >
                  <div className={styles.cardIcon}>
                    <span>{item.icon}</span>
                  </div>

                  <div className={styles.cardContent}>
                    <h4>
                      {item.number}. {item.title}
                    </h4>

                    <p>{item.content}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.bottomBar}>
              🔒 Your data is private, secure,
              and handled with care.
            </div>
          </div>
        </div>

        <div className={styles.footer}>

          <div className={styles.footerLogo}>
            MUNAFA ENGINE
          </div>

          <div className={styles.footerLinks}>
            <button
              onClick={onOpenTerms}
              className={styles.navLink}
            >
              Terms & Conditions
            </button>

            <span className={styles.legalSep}>|</span>

            <button
              onClick={onOpenPrivacy}
              className={styles.navLink}
            >
              Privacy Policy
            </button>
          </div>

          <div className={styles.footerCopy}>
            © 2026 Munafa Engine. All rights
            reserved.
          </div>
        </div>
      </div>
    </Modal>
  );
}
